import { useCallback, useEffect, useState } from 'react';

import {
  Box,
  Drawer,
  type DrawerProps,
  IconButton,
  type SxProps,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type * as CSS from 'csstype';
import { ChevronLeftIcon } from 'lucide-react';

import { useNavigateWithSearchParams } from '@modules/_shared/hooks/useNavigateWithSearchParams';
import { customPalette } from '@shared/theme/StyleProvider';

export type ListingMarketingDrawerProps = React.PropsWithChildren<
  {
    title: string;
    width?: CSS.PropertiesFallback<number | string>['width'];
    slotProps?: {
      container?: {
        sx?: SxProps;
      };
    };
  } & Pick<DrawerProps, 'container'>
>;

export const ListingMarketingDrawer: React.FC<ListingMarketingDrawerProps> = ({
  children,
  container,
  title,
  width,
  slotProps,
}) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const navigate = useNavigateWithSearchParams();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const handleClose = useCallback(() => {
    setOpen(false);
    setTimeout(() => {
      navigate('../');
    }, 300);
  }, [navigate]);

  // Fill the adjacent pane on desktop. Pixel widths are viewport-fixed and
  // crush the in-tab menu because MUI paper defaults to position: fixed.
  const drawerWidth = width ?? (isMobile ? ['100%', '85%'] : '100%');

  // Render Drawer animation
  useEffect(() => setOpen(true), []);

  return (
    <Drawer
      open={open}
      anchor="right"
      variant={isMobile ? 'temporary' : 'permanent'}
      onClose={handleClose}
      container={container}
      sx={{
        width: drawerWidth,
        height: isMobile ? undefined : '100%',
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          boxSizing: 'border-box',
          bgcolor: customPalette.superLightBlue,
          ...(isMobile
            ? {
                paddingTop: 6,
                width: drawerWidth,
              }
            : {
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
              }),
        },
      }}
    >
      {isMobile && (
        <>
          <Toolbar sx={{ minHeight: '64px !important' }} />
          <Toolbar
            sx={{
              minHeight: '48px !important',
              pl: '8px !important',
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <IconButton onClick={handleClose}>
              <ChevronLeftIcon size={20} />
            </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1, px: 1 }} noWrap>
              {title}
            </Typography>
          </Toolbar>
        </>
      )}
      <Box
        sx={[
          {
            overflow: 'auto',
            p: 2,
            flex: 1,
            flexShrink: 0,
          },
          ...(slotProps?.container?.sx != null
            ? Array.isArray(slotProps.container.sx)
              ? slotProps.container.sx
              : [slotProps.container.sx]
            : []),
        ]}
      >
        {children}
      </Box>
    </Drawer>
  );
};
